import type {
  AfterSaleRecord,
  AuditRecord,
  DashboardData,
  EntitlementRecord,
  FeedbackRecord,
  GenerationTaskRecord,
  InventoryRecord,
  OrderRecord,
  PaletteImportRecord,
  ProductRecord,
  ProjectRecord,
  StaffLoginResult,
  StaffLoginSuccess,
  StaffUserRecord,
  SystemConfigRecord,
  TotpSetupResult,
} from './types';

const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '/v1';
const TOKEN_KEY = 'dougrid.staff.accessToken';

interface ApiEnvelope<T> {
  code: string;
  data: T;
  message?: string;
  details?: unknown;
  requestId: string;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// crypto.randomUUID 仅在安全上下文（HTTPS/localhost）可用；HTTP 部署时回退到 getRandomValues
function newRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function request<T>(path: string, init: RequestInit = {}, authenticated = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (authenticated) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('X-Request-Id', newRequestId());

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const payload = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!response.ok || !payload || payload.code !== 'OK') {
    if (response.status === 401) window.dispatchEvent(new Event('dougrid:staff-unauthorized'));
    throw new ApiError(
      payload?.code ?? 'NETWORK_ERROR',
      payload?.message ?? '服务暂时不可用，请稍后重试',
      response.status,
      payload?.requestId,
    );
  }
  return payload.data;
}

function jsonBody(value: unknown): RequestInit {
  return { body: JSON.stringify(value) };
}

function queryString(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

export const staffApi = {
  login: (username: string, password: string, totp?: string) =>
    request<StaffLoginResult>(
      '/staff/auth/login',
      { method: 'POST', ...jsonBody({ username, password, ...(totp ? { totp } : {}) }) },
      false,
    ),
  setupTotp: (enrollmentToken: string) =>
    request<TotpSetupResult>(
      '/staff/auth/totp/setup',
      { method: 'POST', ...jsonBody({ enrollmentToken }) },
      false,
    ),
  confirmTotp: (enrollmentToken: string, code: string) =>
    request<StaffLoginSuccess>(
      '/staff/auth/totp/confirm',
      { method: 'POST', ...jsonBody({ enrollmentToken, code }) },
      false,
    ),
  reauthenticate: (password: string, totp?: string) =>
    request<{ reauthToken: string; expiresIn: number }>('/staff/auth/reauth', {
      method: 'POST',
      ...jsonBody({ password, ...(totp ? { totp } : {}) }),
    }),
  dashboard: (range = '7d') => request<DashboardData>(`/staff/dashboard?range=${range}`),
  orders: (
    params: {
      status?: string | undefined;
      query?: string | undefined;
      mode?: string | undefined;
    } = {},
  ) => request<OrderRecord[]>(`/staff/orders${queryString(params)}`),
  order: (id: string) => request<OrderRecord>(`/staff/orders/${id}`),
  startPacking: (id: string) =>
    request<OrderRecord>(`/staff/orders/${id}/start-packing`, { method: 'POST' }),
  ship: (id: string, input: { carrierCode: string; carrierName: string; trackingNo: string }) =>
    request<ShipmentRecord>(`/staff/orders/${id}/ship`, {
      method: 'POST',
      ...jsonBody(input),
    }),
  inventory: () => request<InventoryRecord[]>('/staff/inventory'),
  adjustInventory: (
    id: string,
    input: { delta: number; reason: string; type: string; reauthToken?: string },
  ) =>
    request<InventoryRecord>(`/staff/inventory/${id}/adjust`, {
      method: 'POST',
      ...jsonBody(input),
    }),
  tasks: (params: { status?: string | undefined; errorCode?: string | undefined } = {}) =>
    request<GenerationTaskRecord[]>(`/staff/generation-tasks${queryString(params)}`),
  retryTask: (id: string, reason: string) =>
    request<GenerationTaskRecord>(`/staff/generation-tasks/${id}/retry`, {
      method: 'POST',
      ...jsonBody({ reason }),
    }),
  audits: (params: { action?: string | undefined; actorId?: string | undefined } = {}) =>
    request<AuditRecord[]>(`/staff/audit-logs${queryString(params)}`),
  products: () => request<ProductRecord[]>('/staff/products'),
  updateProduct: (id: string, input: object) =>
    request<ProductRecord>(`/staff/products/${id}`, { method: 'PATCH', ...jsonBody(input) }),
  paletteImports: () => request<PaletteImportRecord[]>('/staff/palette-imports'),
  createPaletteImport: (file: File) => {
    const form = new FormData();
    form.set('file', file);
    return request<PaletteImportRecord>('/staff/palette-imports', { method: 'POST', body: form });
  },
  publishPaletteImport: (id: string, reason: string, reauthToken: string) =>
    request<PaletteImportRecord>(`/staff/palette-imports/${id}/publish`, {
      method: 'POST',
      ...jsonBody({ reason, reauthToken }),
    }),
  testPaletteImport: (id: string) =>
    request<PaletteImportRecord>(`/staff/palette-imports/${id}/test`, { method: 'POST' }),
  projects: (params: { platform?: string | undefined; query?: string | undefined } = {}) =>
    request<ProjectRecord[]>(`/staff/projects${queryString(params)}`),
  entitlements: (params: { projectId?: string; userId?: string }) =>
    request<EntitlementRecord[]>(`/staff/entitlements${queryString(params)}`),
  grantEntitlement: (projectId: string, reason: string, reauthToken: string) =>
    request<EntitlementRecord>(`/staff/projects/${projectId}/grant-entitlement`, {
      method: 'POST',
      ...jsonBody({ reason, reauthToken }),
    }),
  afterSales: (params: { status?: string | undefined } = {}) =>
    request<AfterSaleRecord[]>(`/staff/after-sales${queryString(params)}`),
  afterSaleAction: (
    id: string,
    input: { action: string; reason: string; refundFen?: number; reauthToken?: string },
  ) =>
    request<AfterSaleRecord>(`/staff/after-sales/${id}/actions`, {
      method: 'POST',
      ...jsonBody(input),
    }),
  feedback: (params: { status?: string | undefined } = {}) =>
    request<FeedbackRecord[]>(`/staff/feedback${queryString(params)}`),
  feedbackAction: (
    id: string,
    input: { status: 'PROCESSING' | 'RESOLVED' | 'CLOSED'; reply: string; reason: string },
  ) =>
    request<FeedbackRecord>(`/staff/feedback/${id}/actions`, {
      method: 'POST',
      ...jsonBody(input),
    }),
  staffUsers: () => request<StaffUserRecord[]>('/staff/users'),
  createStaffUser: (input: object) =>
    request<StaffUserRecord>('/staff/users', { method: 'POST', ...jsonBody(input) }),
  configs: () => request<SystemConfigRecord[]>('/staff/system-configs'),
  updateConfig: (key: string, value: unknown, reason: string, reauthToken: string) =>
    request<SystemConfigRecord>(`/staff/system-configs/${encodeURIComponent(key)}`, {
      method: 'PUT',
      ...jsonBody({ value, reason, reauthToken }),
    }),
};

export { TOKEN_KEY };

interface ShipmentRecord {
  id: string;
  carrier_code: string;
  carrier_name: string;
  tracking_no_masked: string;
  shipped_at: string;
}
